import React, { useState } from 'react';
import { Callout, DefaultButton, DelayedRender, Dialog, DialogFooter, DialogType, DirectionalHint, PrimaryButton, Stack, Text, Toggle } from '@fluentui/react';
import { IC } from 'components/SVGIcon';
import { QuestionCircle } from 'kits/IconKit2';
import IvicosStrings from 'kits/language/stringKit';
import { OrganizationDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import { ZammadSettings } from './Zammad/ZammadSettings';
import type { IZammadConfiguration } from './Zammad/zammad.types';
import { OpenProjectSettings } from './OpenProject/OpenProjectSettings';
import type { IOpenProjectConfiguration } from './OpenProject/openproject.types';

interface IIntegrationSettingsProps {
    enabledProviders: string[];
    updateProvider: (provider: string, enabled: boolean) => Promise<void>;
    isOrgLoaded: boolean;
    isZammadEnabled: boolean;
    zammadBaseUrl: string;
    updateZammadConfig: (config: IZammadConfiguration) => Promise<OrganizationDto>;
    isOpenProjectEnabled: boolean;
    openProjectBaseUrl: string;
    updateOpenProjectConfig: (config: IOpenProjectConfiguration) => Promise<OrganizationDto>;
}

interface IProvider {
    key: string;
    title: string;
    description: string;
    disabled?: boolean;
}

const SALES_EMAIL = 'sales@ivicos.eu';

const openSalesMailCompose = (): void => {
    const mailtoUrl = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent('Google integration')}`;
    const link = document.createElement('a');
    link.href = mailtoUrl;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const IntegrationSettings: React.FC<IIntegrationSettingsProps> = ({
    enabledProviders,
    updateProvider,
    isOrgLoaded,
    isZammadEnabled,
    zammadBaseUrl,
    updateZammadConfig,
    isOpenProjectEnabled,
    openProjectBaseUrl,
    updateOpenProjectConfig
}) => {
    const PROVIDERS: IProvider[] = [
        {
            key: 'microsoft',
            title: IvicosStrings.microsoftProviderTitle,
            description: IvicosStrings.microsoftProviderDescription
        },
        {
            key: 'google',
            title: IvicosStrings.googleProviderTitle,
            description: IvicosStrings.googleProviderDescription,
            disabled: true
        }
    ];

    const [isPending, setIsPending] = useState(false);
    const [activeCallout, setActiveCallout] = useState<string | null>(null);
    const [pendingDisableKey, setPendingDisableKey] = useState<string | null>(null);

    const handleToggle = async (providerKey: string, checked: boolean, isDisabled?: boolean): Promise<void> => {
        if (isDisabled) return;
        if (!checked) {
            setPendingDisableKey(providerKey);
            return;
        }
        setIsPending(true);
        try {
            await updateProvider(providerKey, checked);
        } catch (err) {
            console.error('Failed to update provider:', err);
        } finally {
            setIsPending(false);
        }
    };

    const handleConfirmDisable = async (): Promise<void> => {
        if (!pendingDisableKey) return;
        const keyToDisable = pendingDisableKey;
        setPendingDisableKey(null);
        setIsPending(true);
        try {
            await updateProvider(keyToDisable, false);
        } catch (err) {
            console.error('Failed to update provider:', err);
        } finally {
            setIsPending(false);
        }
    };

    const activeProvider = PROVIDERS.find((p) => p.key === activeCallout);

    return (
        <>
            <Stack tokens={{ childrenGap: 16 }} style={{ margin: 16 }}>
                {PROVIDERS.map((provider) => {
                    const isEnabled = enabledProviders.includes(provider.key);
                    const calloutTargetId = `provider-info-${provider.key}`;
                    const isCalloutOpen = activeCallout === provider.key;
                    return (
                        <Stack key={provider.key} horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }} style={{ minWidth: 140 }}>
                                <Text style={{ fontWeight: 600 }}>{provider.title}</Text>
                                <IC variant="custom" size={15} style={{ cursor: 'pointer' }}>
                                    <QuestionCircle id={calloutTargetId} onClick={() => setActiveCallout(isCalloutOpen ? null : provider.key)} />
                                </IC>
                            </Stack>
                            <Stack.Item grow />
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                <Toggle
                                    checked={isEnabled}
                                    onText={IvicosStrings.providerEnabledLabel}
                                    offText={IvicosStrings.providerDisabledLabel}
                                    disabled={isPending || !isOrgLoaded || provider.disabled}
                                    onChange={(_ev, checked) => handleToggle(provider.key, !!checked, provider.disabled)}
                                    styles={{ root: { minWidth: 120, marginBottom: 0 } }}
                                />
                                {provider.disabled && <PrimaryButton text={IvicosStrings.talkToSales} onClick={openSalesMailCompose} />}
                            </Stack>
                        </Stack>
                    );
                })}
                <ZammadSettings
                    isZammadEnabled={isZammadEnabled}
                    zammadBaseUrl={zammadBaseUrl}
                    updateZammadConfig={updateZammadConfig}
                    isOrgLoaded={isOrgLoaded}
                />
                <OpenProjectSettings
                    isOpenProjectEnabled={isOpenProjectEnabled}
                    openProjectBaseUrl={openProjectBaseUrl}
                    updateOpenProjectConfig={updateOpenProjectConfig}
                    isOrgLoaded={isOrgLoaded}
                />
            </Stack>
            {activeProvider && (
                <Callout
                    target={`#provider-info-${activeProvider.key}`}
                    onDismiss={() => setActiveCallout(null)}
                    directionalHint={DirectionalHint.bottomCenter}
                    style={{ padding: '12px 16px', maxWidth: 280 }}
                    setInitialFocus={false}
                >
                    <DelayedRender>
                        <Text variant="small">{activeProvider.description}</Text>
                    </DelayedRender>
                </Callout>
            )}
            <Dialog
                hidden={pendingDisableKey === null}
                onDismiss={() => setPendingDisableKey(null)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: IvicosStrings.disableProviderDialogTitle.replace('{0}', PROVIDERS.find((p) => p.key === pendingDisableKey)?.title ?? ''),
                    subText: IvicosStrings.disableProviderDialogWarning.replace('{0}', PROVIDERS.find((p) => p.key === pendingDisableKey)?.title ?? '')
                }}
                modalProps={{ isBlocking: true, styles: { main: { maxWidth: '90vw', minWidth: 'min(320px, 90vw)' } } }}
            >
                <DialogFooter>
                    <DefaultButton text={IvicosStrings.disableProviderDialogCancel} onClick={() => setPendingDisableKey(null)} />
                    <DefaultButton
                        text={IvicosStrings.disableProviderDialogConfirm}
                        onClick={handleConfirmDisable}
                        styles={{
                            root: { backgroundColor: 'red', borderColor: 'red', color: 'white' },
                            rootHovered: { backgroundColor: '#cc0000', borderColor: '#cc0000', color: 'white' }
                        }}
                    />
                </DialogFooter>
            </Dialog>
        </>
    );
};
